import React, { useEffect, useCallback } from 'react';
import { Platform } from 'react-native';
import { check, PERMISSIONS, RESULTS } from 'react-native-permissions';
import { useAppDispatch } from '@/hooks/use.redux';
import { actionSetCameraPermission, actionSetMicPermission } from '@/redux/permissions.reducer';
import { useIsForeground } from '@/hooks/use.is.foreground';

const MIC_PERMISSION = PERMISSIONS.ANDROID.RECORD_AUDIO;
const CAMERA_PERMISSION = PERMISSIONS.ANDROID.CAMERA;

function BasePermissionsProvider({ children }: any) {
  const dispatch = useAppDispatch();
  const { isForeground } = useIsForeground();

  const syncPermissionsToRedux = useCallback(async () => {
    // --- WEB LOGIC ---
    if (Platform.OS === 'web') {
      try {
        // Query the browser silently. (Note: Safari has spotty support for this specific query method, but Chrome/Edge/Firefox work perfectly)
        const micStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        const camStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });

        dispatch(actionSetMicPermission({ granted: micStatus.state === 'granted', canAskAgain: micStatus.state === 'prompt' }));
        dispatch(actionSetCameraPermission({ granted: camStatus.state === 'granted', canAskAgain: camStatus.state === 'prompt' }));
      } catch (e) {
        console.warn('Browser does not support silent permission querying.');
      }
      return;
    }

    // --- ANDROID LOGIC ---
    const micStatus = await check(MIC_PERMISSION);
    const cameraStatus = await check(CAMERA_PERMISSION);

    dispatch(
      actionSetMicPermission({
        granted: micStatus === RESULTS.GRANTED,
        canAskAgain: micStatus !== RESULTS.BLOCKED,
      }),
    );

    dispatch(
      actionSetCameraPermission({
        granted: cameraStatus === RESULTS.GRANTED,
        canAskAgain: cameraStatus !== RESULTS.BLOCKED,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (isForeground) {
      syncPermissionsToRedux();
    }
  }, [isForeground, syncPermissionsToRedux]);

  return children;
}

export const PermissionsProvider = React.memo(BasePermissionsProvider);
